<script>
import { generateTimeSlots, isTimeRangeValid } from '../helpers/businessHour';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const timeSlots = generateTimeSlots(30);

const groupByPeriod = slots =>
  ['AM', 'PM']
    .map(period => ({
      label: period,
      options: slots
        .filter(slot => slot.endsWith(period))
        .map(slot => ({ value: slot, label: slot })),
    }))
    .filter(group => group.options.length);

export default {
  components: {
    NextSelect,
    ComboBox,
    Button,
  },
  props: {
    holiday: {
      type: Object,
      required: true,
    },
    botOptions: {
      type: Array,
      default: () => [],
    },
    teamOptions: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update', 'remove'],
  computed: {
    fromTimeSlots() {
      return groupByPeriod(timeSlots);
    },
    toTimeSlots() {
      return groupByPeriod(timeSlots.filter(slot => slot !== '12:00 AM'));
    },
    hasTimeError() {
      return !this.holiday.valid;
    },
    hasRequiredError() {
      return !this.holiday.date || !this.holiday.name?.trim();
    },
    botValue: {
      get() {
        return this.holiday.agentBotId || '';
      },
      set(value) {
        this.emitUpdate({ agentBotId: value || '' });
      },
    },
    teamValue: {
      get() {
        return this.holiday.teamId || '';
      },
      set(value) {
        this.emitUpdate({ teamId: value || '' });
      },
    },
  },
  methods: {
    emitUpdate(changes) {
      const nextHoliday = {
        ...this.holiday,
        ...changes,
      };

      nextHoliday.valid = isTimeRangeValid(nextHoliday.from, nextHoliday.to);
      this.$emit('update', nextHoliday);
    },
    updateField(field, value) {
      this.emitUpdate({ [field]: value });
    },
  },
};
</script>

<template>
  <tr>
    <td colspan="3" class="p-4 bg-n-slate-1/40">
      <div class="flex items-start justify-between gap-4 mb-4">
        <div>
          <p class="mb-1 text-body-main text-n-slate-12 font-medium">
            {{ holiday.name || $t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.NEW') }}
          </p>
          <p class="mb-0 text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.DESCRIPTION') }}
          </p>
        </div>
        <Button
          type="button"
          ghost
          sm
          slate
          icon="i-lucide-trash-2"
          :label="$t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.REMOVE')"
          @click="$emit('remove')"
        />
      </div>

      <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          <span>{{ $t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.DATE') }}</span>
          <input
            :value="holiday.date"
            type="date"
            class="!mb-0"
            @input="updateField('date', $event.target.value)"
          />
        </label>

        <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          <span>{{ $t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.NAME') }}</span>
          <input
            :value="holiday.name"
            type="text"
            class="!mb-0"
            :placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.NAME_PLACEHOLDER')"
            @input="updateField('name', $event.target.value)"
          />
        </label>

        <div class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          <span>{{ $t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.HOURS') }}</span>
          <div class="flex items-center gap-2">
            <NextSelect
              :model-value="holiday.from"
              :groups="fromTimeSlots"
              class="w-full"
              @update:model-value="value => updateField('from', value)"
            />
            <span class="text-n-slate-11">-</span>
            <NextSelect
              :model-value="holiday.to"
              :groups="toTimeSlots"
              class="w-full"
              @update:model-value="value => updateField('to', value)"
            />
          </div>
        </div>

        <div class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          <span>{{ $t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.BOT') }}</span>
          <ComboBox
            v-model="botValue"
            :options="botOptions"
            :placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.NO_BOT')"
            :search-placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.SEARCH_BOT')"
            :empty-state="$t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.NO_BOT_RESULTS')"
          />
        </div>

        <div class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          <span>{{ $t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.TEAM') }}</span>
          <ComboBox
            v-model="teamValue"
            :options="teamOptions"
            :placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.NO_TEAM')"
            :search-placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.SEARCH_TEAM')"
            :empty-state="$t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.NO_TEAM_RESULTS')"
          />
        </div>

        <label
          class="flex flex-col gap-1.5 text-sm text-n-slate-12 md:col-span-2 xl:col-span-3"
        >
          <span>{{
            $t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.ABSENCE_MESSAGE')
          }}</span>
          <textarea
            :value="holiday.absenceMessage"
            rows="3"
            class="!mb-0"
            :placeholder="
              $t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.ABSENCE_MESSAGE_PLACEHOLDER')
            "
            @input="updateField('absenceMessage', $event.target.value)"
          />
        </label>
      </div>

      <p v-if="hasRequiredError" class="mt-3 mb-0 text-xs text-n-ruby-9">
        {{ $t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.REQUIRED_ERROR') }}
      </p>
      <p v-else-if="hasTimeError" class="mt-3 mb-0 text-xs text-n-ruby-9">
        {{ $t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.TIME_ERROR') }}
      </p>
    </td>
  </tr>
</template>
